<template>
  <div v-if="site">
    <div class="mb-8 flex items-center justify-start gap-8">
      <Button type="secondary" @click="$router.push({ name: 'partners.view.sites', params: { id: partner.id } })">
        <Icon icon="chevron-left" />
      </Button>
      <h2 class="page-header-title">{{ site.domain }}</h2>
    </div>
    <Row lg="row">
      <Column lg="2/3">
        <Card title="Site" :noPadding="true">
          <template v-slot:buttons>
            <Button prepend-icon="pencil" @click="editSite()" />
          </template>

          <dl class="dl-horizontal">
            <dt>ID</dt>
            <dd>{{ site.hashid }}</dd>
            <dt>Main Domain</dt>
            <dd>{{ site.domain }}</dd>
            <dt>Allowed Domains</dt>
            <dd>
              <ul v-if="site.copilot_domains" class="list-comma-separated">
                <li v-for="(domain, index) in site.copilot_domains" :key="index">{{ domain }}</li>
              </ul>
              <span v-else class="text-supplement">-</span>
            </dd>
            <dt>Primary Site</dt>
            <dd>
              <Icon
                :icon="site.primary ? 'check' : 'times'"
                :class="site.primary ? 'text-success' : 'text-supplement'"
              />
            </dd>
            <dt>Featured Image</dt>
            <dd>
              <Icon
                :icon="site.featured_image ? 'check' : 'times'"
                :class="site.featured_image ? 'text-success' : 'text-supplement'"
              />
            </dd>
            <dt>Copilot CSS</dt>
            <dd>
              <Icon
                :icon="site.copilot_css ? 'check' : 'times'"
                :class="site.copilot_css ? 'text-success' : 'text-supplement'"
              />
            </dd>
            <dt>Disclaimer Enabled</dt>
            <dd>
              <Icon
                :icon="site.copilot_disclaimer_enabled ? 'check' : 'times'"
                :class="site.copilot_disclaimer_enabled ? 'text-success' : 'text-supplement'"
              />
            </dd>
            <template v-if="site.copilot_disclaimer_enabled">
              <dt>Disclaimer</dt>
              <dd>{{ site.copilot_disclaimer_text || "Default" }}</dd>
              <dt>Disclaimer Selector</dt>
              <dd>{{ site.copilot_disclaimer_selector || "body" }}</dd>
            </template>

            <dt>Script Translations</dt>
            <dd>
              <ul v-if="site.enabled_translations" class="list-comma-separated">
                <li v-for="translation in site.enabled_translations" :key="translation.id">
                  {{ translation.name }} {{ translation.icon }}
                </li>
              </ul>
              <span v-else class="text-supplement">{{ site.translations_enabled }}</span>
            </dd>
          </dl>
        </Card>

        <!-- JavaScript Transformations -->
        <DataTable
          ref="transformationsDataTable"
          title="JavaScript Transformations"
          :endpoint="`partners/${partner.id}/sites/${site.id}/transformations`"
        >
          <template v-slot:buttons>
            <Button type="primary" @click="newPartnerSiteTransformation()"><Icon icon="plus" /></Button>
          </template>

          <template v-slot:rows="rows">
            <DataTableRow v-for="(row, i) in rows" :key="i" :row="row">
              <DataTableColumn title="Name" sortable="name">{{ row.name }}</DataTableColumn>
              <DataTableColumn title="Language">{{
                (row.language && row.language.name) || "All Languages"
              }}</DataTableColumn>
              <DataTableColumn title="Href">
                <Icon
                  :icon="!row.advisor_cta && row.href ? 'check' : 'times'"
                  :class="!row.advisor_cta && row.href ? 'text-success' : 'text-supplement'"
                />
              </DataTableColumn>
              <DataTableColumn title="Text">
                <Icon :icon="row.text ? 'check' : 'times'" :class="row.text ? 'text-success' : 'text-supplement'" />
              </DataTableColumn>
              <DataTableColumn title="Advisor CTA" sortable="advisor_cta">
                <Icon
                  :icon="row.advisor_cta ? 'check' : 'times'"
                  :class="row.advisor_cta ? 'text-success' : 'text-supplement'"
                />
              </DataTableColumn>
              <DataTableColumn title="Replace" sortable="replace_element">
                <Icon
                  :icon="row.replace_element ? 'check' : 'times'"
                  :class="row.replace_element ? 'text-success' : 'text-supplement'"
                />
              </DataTableColumn>
              <DataTableColumn title="Enabled" sortable="enabled">
                <Icon
                  :icon="row.enabled ? 'check' : 'times'"
                  :class="row.enabled ? 'text-success' : 'text-supplement'"
                />
              </DataTableColumn>
              <template v-slot:actions>
                <Dropdown icon="ellipsis-v" class="flip-menu">
                  <DropdownLink icon="pencil" @click="editPartnerSiteTransformation(row.id)">Edit</DropdownLink>
                  <DropdownLink icon="trash" @click="deletePartnerSiteTransformation(row)">Delete</DropdownLink>
                </Dropdown>
              </template>
            </DataTableRow>
          </template>
        </DataTable>
        <!-- Redirects -->
        <DataTable
          ref="redirectsDataTable"
          title="Redirects"
          :endpoint="`partners/${partner.id}/sites/${site.id}/redirects`"
        >
          <template v-slot:buttons>
            <Button type="primary" @click="newPartnerSiteRedirect()"><Icon icon="plus" /></Button>
          </template>

          <template v-slot:rows="rows">
            <DataTableRow v-for="(row, i) in rows" :key="i" :row="row">
              <DataTableColumn title="Redirect" sortable="path_pattern">{{ row.path_pattern }}</DataTableColumn>
              <DataTableColumn title="To" sortable="redirect_url">{{ row.redirect_url }}</DataTableColumn>
              <DataTableColumn title="Exact Match" sortable="use_exact_match">
                <Icon
                  :icon="row.use_exact_match ? 'check' : 'times'"
                  :class="row.use_exact_match ? 'text-success' : 'text-supplement'"
                />
              </DataTableColumn>
              <DataTableColumn title="Ignore Previous Page" sortable="ignore_previous_page">
                <Icon
                  :icon="row.ignore_previous_page ? 'check' : 'times'"
                  :class="row.ignore_previous_page ? 'text-success' : 'text-supplement'"
                />
              </DataTableColumn>
              <DataTableColumn title="Enabled" sortable="enabled">
                <Icon
                  :icon="row.enabled ? 'check' : 'times'"
                  :class="row.enabled ? 'text-success' : 'text-supplement'"
                />
              </DataTableColumn>
              <template v-slot:actions>
                <Dropdown icon="ellipsis-v" class="flip-menu">
                  <DropdownLink icon="pencil" @click="editPartnerSiteRedirect(row.id)">Edit</DropdownLink>
                  <DropdownLink icon="trash" @click="deletePartnerSiteRedirect(row)">Delete</DropdownLink>
                </Dropdown>
              </template>
            </DataTableRow>
          </template>
        </DataTable>
        <!-- Custom JS -->
        <DataTable
          ref="customScriptsDataTable"
          title="Custom Scripts"
          :endpoint="`partners/${partner.id}/sites/${site.id}/custom-scripts`"
        >
          <template v-slot:buttons>
            <Button type="primary" @click="newPartnerSiteCustomScript()"><Icon icon="plus" /></Button>
          </template>

          <template v-slot:rows="rows">
            <DataTableRow v-for="(row, i) in rows" :key="i" :row="row">
              <DataTableColumn title="Target" sortable="target" width="max">
                <span v-if="row.target" class="text-xs font-medium uppercase text-primary">{{
                  row.target | ucwords
                }}</span>
                <span v-else class="text-xs font-medium uppercase text-supplement">All</span>
              </DataTableColumn>
              <DataTableColumn title="Description" sortable="description">{{ row.description }}</DataTableColumn>
              <DataTableColumn title="Size" width="max">{{ row.size }}</DataTableColumn>
              <DataTableColumn title="Enabled" sortable="enabled" width="max">
                <Icon
                  :icon="row.enabled ? 'check' : 'times'"
                  :class="row.enabled ? 'text-success' : 'text-supplement'"
                />
              </DataTableColumn>
              <DataTableColumn title="Updated" width="max" sortable="updated_at">{{
                row.updated_at | datetime
              }}</DataTableColumn>
              <template v-slot:actions>
                <Dropdown icon="ellipsis-v" class="flip-menu">
                  <DropdownLink icon="pencil" @click="editPartnerSiteCustomScript(row.id)">Edit</DropdownLink>
                  <DropdownLink icon="trash" @click="deletePartnerSiteCustomScript(row)">Delete</DropdownLink>
                </Dropdown>
              </template>
            </DataTableRow>
          </template>
        </DataTable>
        <!-- Custom Styles -->
        <DataTable
          ref="customStylesDataTable"
          title="Custom Styles"
          :endpoint="`partners/${partner.id}/sites/${site.id}/custom-styles`"
        >
          <template v-slot:buttons>
            <Button type="primary" @click="newPartnerSiteCustomStyle()"><Icon icon="plus" /></Button>
          </template>

          <template v-slot:rows="rows">
            <DataTableRow v-for="(row, i) in rows" :key="i" :row="row">
              <DataTableColumn title="Target" sortable="target" width="max">
                <span v-if="row.target" class="text-xs font-medium uppercase text-primary">{{
                  row.target | ucwords
                }}</span>
                <span v-else class="text-xs font-medium uppercase text-supplement">All</span>
              </DataTableColumn>
              <DataTableColumn title="Description" sortable="description">{{ row.description }}</DataTableColumn>
              <DataTableColumn title="Size" width="max">{{ row.size }}</DataTableColumn>
              <DataTableColumn title="Enabled" sortable="enabled" width="max">
                <Icon
                  :icon="row.enabled ? 'check' : 'times'"
                  :class="row.enabled ? 'text-success' : 'text-supplement'"
                />
              </DataTableColumn>
              <DataTableColumn title="Updated" width="max" sortable="updated_at">{{
                row.updated_at | datetime
              }}</DataTableColumn>
              <template v-slot:actions>
                <Dropdown icon="ellipsis-v" class="flip-menu">
                  <DropdownLink icon="pencil" @click="editPartnerSiteCustomStyle(row.id)">Edit</DropdownLink>
                  <DropdownLink icon="trash" @click="deletePartnerSiteCustomStyle(row)">Delete</DropdownLink>
                </Dropdown>
              </template>
            </DataTableRow>
          </template>
        </DataTable>

        <CustomTranslationsDataTable :partner="partner" :site="site" />
      </Column>
      <Column lg="1/3">
        <Card title="Script" :noPadding="true">
          <template v-slot:buttons>
            <div class="mt-6 mr-4 text-xs text-supplement">
              <ToggleSwitch v-model="useCdn" label="Use CDN" />
            </div>
          </template>

          <div class="flex flex-col divide-y divide-gray-200">
            <div v-for="script in scripts" :key="script.name" class="p-8">
              <div class="mb-4 font-bold">{{ script.name }}</div>
              <div class="flex gap-2">
                <TextInput :value="script.url" readonly class="flex-grow" />
                <Dropdown class="flip-menu dropdown-button">
                  <DropdownLink icon="code-simple" @click="copyScriptTag(script)">Copy Script Tag</DropdownLink>
                  <DropdownHorizontalRule />
                  <DropdownLink icon="browser" @click="openUrl(script)">View Script</DropdownLink>
                  <DropdownLink icon="server" @click="viewCdnHeaders()">CDN Headers</DropdownLink>
                  <DropdownLink icon="refresh" @click="purgeCdnCache(script)">Purge CDN</DropdownLink>
                </Dropdown>
              </div>
            </div>
            <div v-show="false && useCdn" class="border-t border-gray-200 p-8">
              <p>To be completed.</p>
              <Button>Clear CDN Cache</Button>
            </div>
          </div>
        </Card>
      </Column>
    </Row>
  </div>
</template>

<script>
import CustomTranslationsDataTable from "../../../../components/partners/sites/CustomTranslationsDataTable.vue"
import EditPartnerSiteModal from "@/modals/Partners/EditPartnerSiteModal.vue"
import NewPartnerSiteTransformationModal from "@/modals/Partners/NewPartnerSiteTransformationModal.vue"
import EditPartnerSiteTransformationModal from "@/modals/Partners/EditPartnerSiteTransformationModal.vue"
import NewPartnerSiteRedirectModal from "@/modals/Partners/NewPartnerSiteRedirectModal.vue"
import EditPartnerSiteRedirectModal from "@/modals/Partners/EditPartnerSiteRedirectModal.vue"
import NewPartnerSiteCustomScriptModal from "../../../../modals/Partners/NewPartnerSiteCustomScriptModal.vue"
import EditPartnerSiteCustomScriptModal from "@/modals/Partners/EditPartnerSiteCustomScriptModal.vue"
import NewPartnerSiteCustomStyleModal from "../../../../modals/Partners/NewPartnerSiteCustomStyleModal.vue"
import EditPartnerSiteCustomStyleModal from "@/modals/Partners/EditPartnerSiteCustomStyleModal.vue"
import PartnerSiteCdnHeadersModal from "../../../../modals/Partners/PartnerSiteCdnHeadersModal.vue"

export default {
  components: {
    CustomTranslationsDataTable
  },
  metaInfo() {
    return { title: `${this.partner.name} Site` }
  },
  props: {
    partner: null
  },
  data: () => ({
    useCdn: true,
    site: null
  }),
  async created() {
    let response = await this.$api.get(`partners/${this.partner.id}/sites/${this.$route.params.site_id}`)
    this.site = response.data.data
  },
  computed: {
    baseUrl() {
      const hostname = window.location.hostname.replace("admin.", "")
      return this.useCdn ? `https://code.${hostname}` : `https://api.${hostname}/scripts`
    },
    loaderUrl() {
      return `${this.baseUrl}/${this.partner.hash_id}/${this.site.hashid}/loader.js`
    },
    scripts() {
      return [{ name: "Loader", url: this.loaderUrl }]
    }
  },
  methods: {
    async editSite() {
      const response = await this.$modal.show(EditPartnerSiteModal, {
        endpoint: `partners/${this.partner.id}/sites/${this.site.id}`
      })

      if (response) {
        this.site = response.data
      }
    },
    async deleteSite() {
      if (
        await this.$modal.confirm({
          type: "danger",
          message: `Are you sure you want to delete **${this.site.domain}**? This is **irreversible and will permanently delete** this site and all associated data such as transformations.`,
          buttonText: "Delete"
        })
      ) {
        if (await this.$api.delete(`partners/${this.partner.id}/sites/${this.site.id}`)) {
          this.$notification.success(`Site **${this.site.domain}** successfully deleted.`)
          this.$router.push({ name: "partners.view.sites", params: { id: this.partner.id } })
        }
      }
    },
    async newPartnerSiteRedirect() {
      if (
        await this.$modal.show(NewPartnerSiteRedirectModal, {
          partner_id: this.partner.id,
          site_id: this.site.id
        })
      ) {
        this.$refs.redirectsDataTable.reload()
      }
    },
    async editPartnerSiteRedirect(id) {
      if (
        await this.$modal.show(EditPartnerSiteRedirectModal, {
          partner_id: this.partner.id,
          site_id: this.site.id,
          redirect_id: id
        })
      ) {
        this.$refs.redirectsDataTable.reload()
      }
    },
    async deletePartnerSiteRedirect(row) {
      if (
        await this.$modal.confirm({
          type: "danger",
          message: `Are you sure you want to delete this redirect?`,
          buttonText: "Delete"
        })
      ) {
        if (await this.$api.delete(`partners/${this.partner.id}/sites/${this.site.id}/redirects/${row.id}`)) {
          this.$notification.success(`Redirect successfully deleted.`)
          this.$refs.redirectsDataTable.reload()
        }
      }
    },
    async newPartnerSiteTransformation() {
      if (
        await this.$modal.show(NewPartnerSiteTransformationModal, {
          partner_id: this.partner.id,
          site_id: this.site.id
        })
      ) {
        this.$refs.transformationsDataTable.reload()
      }
    },
    async editPartnerSiteTransformation(id) {
      if (
        await this.$modal.show(EditPartnerSiteTransformationModal, {
          partner_id: this.partner.id,
          site_id: this.site.id,
          transformation_id: id
        })
      ) {
        this.$refs.transformationsDataTable.reload()
      }
    },
    async deletePartnerSiteTransformation(row) {
      if (
        await this.$modal.confirm({
          type: "danger",
          message: `Are you sure you want to delete **${row.name}**?`,
          buttonText: "Delete"
        })
      ) {
        if (await this.$api.delete(`partners/${this.partner.id}/sites/${this.site.id}/transformations/${row.id}`)) {
          this.$notification.success(`**${row.name}** successfully deleted.`)
          this.$refs.transformationsDataTable.reload()
        }
      }
    },
    async newPartnerSiteCustomScript() {
      await this.$modal.show(NewPartnerSiteCustomScriptModal, {
        partner_id: this.partner.id,
        site_id: this.site.id
      })

      this.$refs.customScriptsDataTable.reload()
    },
    async editPartnerSiteCustomScript(id) {
      await this.$modal.show(EditPartnerSiteCustomScriptModal, {
        partner_id: this.partner.id,
        site_id: this.site.id,
        custom_js_id: id
      })

      this.$refs.customScriptsDataTable.reload()
    },
    async deletePartnerSiteCustomScript(row) {
      if (
        await this.$modal.confirm({
          type: "danger",
          message: `Are you sure you want to delete **${row.description}**?`,
          buttonText: "Delete"
        })
      ) {
        if (await this.$api.delete(`partners/${this.partner.id}/sites/${this.site.id}/custom-scripts/${row.id}`)) {
          this.$notification.success(`Custom script **${row.description}** successfully deleted.`)
          this.$refs.customScriptsDataTable.reload()
        }
      }
    },
    async newPartnerSiteCustomStyle() {
      await this.$modal.show(NewPartnerSiteCustomStyleModal, {
        partner_id: this.partner.id,
        site_id: this.site.id
      })

      this.$refs.customStylesDataTable.reload()
    },
    async editPartnerSiteCustomStyle(id) {
      await this.$modal.show(EditPartnerSiteCustomStyleModal, {
        partner_id: this.partner.id,
        site_id: this.site.id,
        custom_style_id: id
      })

      this.$refs.customStylesDataTable.reload()
    },
    async deletePartnerSiteCustomStyle(row) {
      if (
        await this.$modal.confirm({
          type: "danger",
          message: `Are you sure you want to delete **${row.description}**?`,
          buttonText: "Delete"
        })
      ) {
        if (await this.$api.delete(`partners/${this.partner.id}/sites/${this.site.id}/custom-styles/${row.id}`)) {
          this.$notification.success(`Custom style(s) for **${row.description}** have been deleted.`)
          this.$refs.customStylesDataTable.reload()
        }
      }
    },
    copyScriptTag(script) {
      const tagName = "script"
      navigator.clipboard.writeText(`<${tagName} src="${script.url}"></${tagName}>`)
      this.$notification.success(`Copied ${tagName} tag for **${script.name}**!`)
    },
    openUrl(script) {
      window.open(script.url, "_blank")
      this.$notification.success("Opened **" + script.name + "** script!")
    },
    viewCdnHeaders() {
      this.$modal.show(PartnerSiteCdnHeadersModal, {
        partnerId: this.partner.id,
        siteId: this.site.id
      })
    },
    async purgeCdnCache() {
      this.$notification.info("Purging CDN cache for **" + this.partner.name + "**...")
      try {
        await this.$api.post(`partners/${this.partner.id}/sites/${this.site.id}/purge-cdn-cache`)
        this.$notification.success("CDN cache for **" + this.partner.name + "** purged!")
      } catch (error) {
        let message = "Failed to purge CDN cache for **" + this.partner.name + "**"
        if (error.response.data?.message) {
          message += ": " + error.response.data.message
        }
        this.$notification.danger(message)
      }
    }
  }
}
</script>

<style>
.dropdown-button .dropdown-trigger {
  @apply flex h-full w-12 items-center justify-center rounded border hover:bg-gray-100;
}
</style>
